import React, { useEffect, useState } from "react";
import { FieldArray, FormikProvider, FormikErrors, useFormik } from "formik";
import * as Yup from "yup";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MuiTelInput } from "mui-tel-input";
import CountryAutocomplete from "../../../components/country-autocomplete";
import { useAccountQuery } from "../../../features/auth/authApi";
import { checkCreds } from "../../../service/api";

interface Director {
  position: string;
  nationality: string;
  fullName: string;
  dateOfBirth: string;
  address: {
    country: string;
    address: string;
    zip: string;
  };
  email: string;
  mobileNumber: string;
}

interface ManagementFormValues {
  directors: Director[];
}

const initialValues: ManagementFormValues = {
  directors: [
    {
      position: "",
      nationality: "",
      fullName: "",
      dateOfBirth: "",
      address: { country: "", address: "", zip: "" },
      email: "",
      mobileNumber: "",
    },
  ],
};

const validationSchema = Yup.object().shape({
  directors: Yup.array()
    .of(
      Yup.object().shape({
        position: Yup.string().required("Required"),
        nationality: Yup.string().required("Required"),
        fullName: Yup.string().required("Required"),
        dateOfBirth: Yup.date()
          .required("Required")
          .typeError("Required")
          .max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            "Must be at least 18"
          )
          .min(
            new Date(new Date().setFullYear(new Date().getFullYear() - 110)),
            "Age seems invalid"
          ),
        address: Yup.object().shape({
          country: Yup.string().required("Country required"),
          address: Yup.string().required("Address required"),
          zip: Yup.string().required("Zip code required"),
        }),
        email: Yup.string().email("Invalid email").required("Required"),
        mobileNumber: Yup.string().required("Required"),
      })
    )
    .test(
      "unique-emails",
      "Each director must have a unique email",
      (directors) => {
        if (!directors) return true;
        const emails = directors.map((d) => d.email);
        return emails.length === new Set(emails).size;
      }
    )
    .test(
      "unique-phones",
      "Each director must have a unique phone number",
      (directors) => {
        if (!directors) return true;
        const phones = directors.map((d) => d.mobileNumber);
        return phones.length === new Set(phones).size;
      }
    ),
});

const CompanyInformationForm4 = () => {
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();
  const onSubmit = async (values: ManagementFormValues) => {
    setDuplicateError(null);
    setDuplicateWarning(null);

    let blockSubmit = false;
    let warnings: string[] = [];

    const currentLogin = account?.user?.login;
    const hasForm3 = !!account?.user?.form3_json;

    for (const director of values.directors) {
      try {
        await checkCreds({
          email: director.email,
          phone: director.mobileNumber,
        });
      } catch (err: any) {
        console.error("Check creds error", err);

        const message =
          typeof err === "string"
            ? err
            : err?.message || "Unknown error checking credentials";

        if (!hasForm3 && director.email !== currentLogin) {
          blockSubmit = true;
          setDuplicateError(`${message}`);
          break;
        } else {
          warnings.push(`Duplicate Entry - without changes`);
        }
      }
    }

    if (blockSubmit) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (warnings.length > 0) {
      setDuplicateWarning(warnings.join("\n"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));

    const latestData = JSON.parse(localStorage.getItem("form3_json") || "{}");

    const mergedData = {
      ...latestData,
      directors: values.directors,
      ...(localStorage.getItem("managementStructure")
        ? { isDirector: true }
        : {}),
    };

    localStorage.setItem("form3_json", JSON.stringify(mergedData));

    navigate("/separate-corp-info-5");
  };

  const formik = useFormik<ManagementFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const errorTextDirector = (index: number, field: keyof Director): string => {
    const touchedObj = formik.touched.directors?.[index];
    const errorObj = formik.errors.directors?.[index];
    if (touchedObj && errorObj && typeof errorObj !== "string") {
      const fieldError = (errorObj as FormikErrors<Director>)[field];
      return fieldError ? (fieldError as string) : "";
    }
    return "";
  };

  useEffect(() => {
    let directors: Director[] = [];
    const acctJson = account?.user.form3_json;
    if (acctJson) {
      try {
        const parsed =
          typeof acctJson === "object" ? acctJson : JSON.parse(acctJson);
        if (Array.isArray(parsed.directors)) {
          directors = parsed.directors;
        }
      } catch {
        console.warn("Cant parse form3_json");
      }
    }

    if (directors.length === 0) {
      const stored = localStorage.getItem("managementStructure");
      if (stored) {
        try {
          const arr = JSON.parse(stored);
          if (Array.isArray(arr) && arr.length > 0) {
            const o = arr[0];
            directors = [
              {
                position: "",
                nationality: o.nationality || "",
                fullName: o.fullName || "",
                dateOfBirth: o.date_of_birth || "",
                address: {
                  country: o.country || "",
                  address: `${o.address || ""} ${o.city || ""} ${
                    o.state || ""
                  }`,
                  zip: o.zip || "",
                },
                email: o.email || "",
                mobileNumber: o.phone || "",
              },
            ];
          }
        } catch {
          console.warn("Cant parse ls");
        }
      }
    }

    if (directors.length > 0) {
      formik.setValues({ directors });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikProvider value={formik}>
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <Container maxWidth="md">
          <form onSubmit={formik.handleSubmit}>
            <Card
              elevation={24}
              component={motion.div}
              layoutId="management-structure-card"
            >
              {duplicateError && (
                <Alert severity="error" sx={{ whiteSpace: "pre-line", mb: 2 }}>
                  {duplicateError}
                </Alert>
              )}

              {duplicateWarning && (
                <Alert
                  severity="warning"
                  sx={{ whiteSpace: "pre-line", mb: 2 }}
                >
                  {duplicateWarning}
                </Alert>
              )}
              <CardContent>
                <Stack spacing={4}>
                  <Typography variant="h5" fontWeight="600">
                    Management Structure
                  </Typography>
                  <Typography>
                    Please confirm the names and addresses of the directors of
                    the company
                  </Typography>

                  <FieldArray name="directors">
                    {({ push, remove, form }) => (
                      <>
                        {form.values.directors.map(
                          (director: any, index: any) => (
                            <Box key={index} sx={{ mt: 3 }}>
                              <Typography variant="h6">
                                Director {index + 1}
                              </Typography>
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Position"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `directors[${index}].position`
                                    )}
                                    error={
                                      !!errorTextDirector(index, "position")
                                    }
                                    helperText={errorTextDirector(
                                      index,
                                      "position"
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <CountryAutocomplete
                                    label="Nationality"
                                    {...formik.getFieldProps(
                                      `directors[${index}].nationality`
                                    )}
                                    value={
                                      formik.values.directors[index].nationality
                                    }
                                    kycProgramId={
                                      account?.user?.wallet?.kyc_program_id
                                    }
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Full Name"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `directors[${index}].fullName`
                                    )}
                                    error={
                                      !!errorTextDirector(index, "fullName")
                                    }
                                    helperText={errorTextDirector(
                                      index,
                                      "fullName"
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                  <TextField
                                    label="Date of Birth"
                                    type="date"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    {...formik.getFieldProps(
                                      `directors[${index}].dateOfBirth`
                                    )}
                                    error={
                                      !!errorTextDirector(index, "dateOfBirth")
                                    }
                                    helperText={errorTextDirector(
                                      index,
                                      "dateOfBirth"
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                  <CountryAutocomplete
                                    label="Country"
                                    {...formik.getFieldProps(
                                      `directors[${index}].address.country`
                                    )}
                                    value={
                                      formik.values.directors[index].address
                                        .country
                                    }
                                    kycProgramId={
                                      account?.user?.wallet?.kyc_program_id
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="Address"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `directors[${index}].address.address`
                                    )}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <TextField
                                    label="ZIP code"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `directors[${index}].address.zip`
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Email"
                                    fullWidth
                                    {...formik.getFieldProps(
                                      `directors[${index}].email`
                                    )}
                                    error={!!errorTextDirector(index, "email")}
                                    helperText={errorTextDirector(
                                      index,
                                      "email"
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <MuiTelInput
                                    forceCallingCode
                                    focusOnSelectCountry
                                    defaultCountry="US"
                                    label="Mobile Number"
                                    name={`directors[${index}].mobileNumber`}
                                    value={
                                      formik.values.directors[index]
                                        .mobileNumber
                                    }
                                    onChange={(val) =>
                                      formik.setFieldValue(
                                        `directors[${index}].mobileNumber`,
                                        val
                                      )
                                    }
                                    error={
                                      !!errorTextDirector(index, "mobileNumber")
                                    }
                                    helperText={errorTextDirector(
                                      index,
                                      "mobileNumber"
                                    )}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                              {form.values.directors.length > 1 && (
                                <Button
                                  variant="outlined"
                                  onClick={() => remove(index)}
                                  sx={{ mt: 1 }}
                                >
                                  Remove Director
                                </Button>
                              )}
                            </Box>
                          )
                        )}
                        <Button
                          variant="contained"
                          onClick={() =>
                            push({
                              position: "",
                              nationality: "",
                              fullName: "",
                              dateOfBirth: "",
                              address: { country: "", address: "", zip: "" },
                              email: "",
                              mobileNumber: "",
                            })
                          }
                          sx={{ mt: 2 }}
                        >
                          Add Director
                        </Button>
                        {typeof formik.errors.directors === "string" && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1 }}
                          >
                            {formik.errors.directors}
                          </Typography>
                        )}
                      </>
                    )}
                  </FieldArray>
                  <Stack spacing={2} mt={3}>
                    <LoadingButton
                      loading={formik.isSubmitting}
                      type="submit"
                      variant="contained"
                    >
                      Save &amp; Continue
                    </LoadingButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </form>
        </Container>
      </Box>
    </FormikProvider>
  );
};

export default CompanyInformationForm4;
